import { BaseTeam } from "@redotech/account-common/base-team";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { Autocomplete } from "@redotech/redo-web/autocomplete";
import { filterTruthy } from "@redotech/util/array";
import { memo, SyntheticEvent, useContext, useMemo, useState } from "react";
import { RedoAdminClient } from "../../../client";
import { RedoAdminClientContext } from "../../../client/context";
import { getCollections } from "../../../client/team";
import { TeamContext } from "../../team";

export interface Collection {
  id: string;
  handle: string;
  title: string;
}

function useCollections(
  team: BaseTeam | undefined,
  client: RedoAdminClient,
  keyFn: (collection: Collection) => string,
) {
  const [searchString, setSearchString] = useState<string>();

  const collectionsLoad = useLoad(async () => {
    if (!team) {
      return { collections: [] };
    }

    return { collections: await getCollections(client, { teamId: team._id }) };
  }, [team, client]);

  const keyMap: Map<string, Collection> = useMemo(
    () =>
      new Map(
        collectionsLoad.value?.collections.map((collection) => [
          keyFn(collection),
          collection,
        ]),
      ),
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collectionsLoad.value],
  );
  const searchMap: Map<string, Collection> = useMemo(
    () =>
      new Map(
        collectionsLoad.value?.collections.map((collection) => [
          collection.title,
          collection,
        ]),
      ),
    [collectionsLoad.value],
  );

  const searchedCollections = useMemo(
    () =>
      searchString
        ? Array.from(searchMap.entries())
            .filter(([title, _]) =>
              title.toLowerCase().includes(searchString.toLowerCase()),
            )
            .map(([_, collection]) => keyFn(collection))
        : Array.from(keyMap.keys()),
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchString, searchMap, keyMap],
  );

  const collectionsLoading = collectionsLoad.pending && !collectionsLoad.value;

  const handleInputChange = useHandler(
    (event: SyntheticEvent<Element, Event>, value: string) => {
      setSearchString(value);
    },
  );

  const getTitle = (key: string) => {
    return keyMap.get(key)?.title;
  };

  return {
    collectionsLoading,
    searchedCollections,
    keyMap,
    handleInputChange,
    getTitle,
  };
}

export const CollectionAutocomplete = memo(function CollectionAutocomplete({
  value,
  setValue,
  setValueTitles,
  setValueCollections,
  keyFn,
  id,
}: {
  value: string[];
  setValue?: (handles: string[]) => void;
  setValueTitles?: (titles: string[]) => void;
  setValueCollections?: (value: Collection[]) => void;
  keyFn: (value: Collection) => string;
  id?: string;
}) {
  const team = useContext(TeamContext);
  const client = useRequiredContext(RedoAdminClientContext);

  const {
    collectionsLoading,
    searchedCollections,
    keyMap,
    handleInputChange,
    getTitle,
  } = useCollections(team, client, keyFn);

  const handleSetValue = (value: string[]) => {
    if (setValueCollections) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setValueCollections(value.map((key) => keyMap.get(key)!));
    }
    if (setValue) {
      setValue(value);
    }
    if (setValueTitles) {
      setValueTitles(filterTruthy(value.map((key) => keyMap.get(key)?.title)));
    }
  };

  return (
    <Autocomplete
      getLabel={(handle) => getTitle(handle) || handle}
      id={id}
      keyFn={(handle) => handle}
      multiple
      noOptionsText={collectionsLoading ? "Loading..." : "No options"}
      onInputChange={handleInputChange}
      options={searchedCollections}
      value={value}
      valueChange={handleSetValue}
    >
      {(handle) => getTitle(handle) || handle}
    </Autocomplete>
  );
});

export const SingleCollectionAutocomplete = memo(
  function SingleCollectionAutocomplete({
    value,
    setValue,
    keyFn,
  }: {
    value: string | undefined;
    setValue: (value: string | undefined) => void;
    keyFn: (collection: Collection) => string;
  }) {
    const team = useContext(TeamContext);
    const client = useRequiredContext(RedoAdminClientContext);

    const {
      collectionsLoading,
      searchedCollections,
      handleInputChange,
      getTitle,
    } = useCollections(team, client, keyFn);

    const handleSetValue = (value: string | null) => {
      setValue(value ?? undefined);
    };

    return (
      <Autocomplete
        getLabel={(handle) => getTitle(handle) || handle}
        keyFn={(handle) => handle}
        noOptionsText={collectionsLoading ? "Loading..." : "No options"}
        onInputChange={handleInputChange}
        options={searchedCollections}
        value={value}
        valueChange={handleSetValue}
      >
        {(handle) => getTitle(handle) || handle}
      </Autocomplete>
    );
  },
);
